/**
 * PATH: apps/quote/src/pages/import-quote/index.tsx
 * FILE_ID: QUOTE-IMPORT-PAGE-001
 * PURPOSE: Quote import page — Excel Parse onto a job, paste AI, existing copy.
 * WHY: Bravada Excel is already parsed by quote-builder. This door reuses that.
 * FLOW: job + file -> upload allow_ai=false -> /jobs/:id?tab=qa
 */
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Save, Loader2 } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { quoteImportApi, type ImportPreview } from '@/lib/api/quote-import-api'
import { ImportInputPanel, type ImportInputMode } from './ImportInputPanel'
import { ImportReviewTable } from './ImportReviewTable'

const JOBS_HUB = 'https://dashboard.quotech.ai'

export default function ImportQuotePage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const jobId = params.get('jobId') || params.get('job') || null
  const jobLabel = params.get('jobNo') || params.get('jobLabel') || (jobId ? `#${jobId}` : null)

  const [mode, setMode] = useState<ImportInputMode>('upload')
  const [text, setText] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)

  const parseMutation = useMutation({
    mutationFn: async () => {
      setParseError(null)
      if (mode === 'paste') return quoteImportApi.parseText(text)
      if (!file) throw new Error('Choose a quote file first.')
      if (!jobId) throw new Error('Create a job first, then parse the Excel onto it.')
      await quoteImportApi.uploadQuoteSheetToJob(jobId, file)
      return null
    },
    onSuccess: (data) => {
      if (data === null) {
        window.location.assign(`${JOBS_HUB}/jobs/${jobId}?tab=qa`)
        return
      }
      if (!data.sections?.length) {
        setParseError('No line items could be read from this document.')
        return
      }
      setPreview(data)
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: string; detail?: string } }; message?: string }
      if (!err?.response) {
        setParseError(
          'The server never returned a readable answer. The request was blocked, timed out, or the gateway closed it.',
        )
        return
      }
      setParseError(
        err.response.data?.error
        || err.response.data?.detail
        || err.message
        || 'Failed to parse the quote.',
      )
    },
  })

  const commitMutation = useMutation({
    mutationFn: () => quoteImportApi.commit({ preview: preview as ImportPreview, jobId, useAsJobBudget: true }),
    onSuccess: (res) => {
      toast.success(`Created ${res.quote_number}`)
      navigate(`/quotes/${res.quote_id}`)
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || e?.message || 'Failed to save the quote.'),
  })

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => (preview ? setPreview(null) : navigate(-1))}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 mb-1">
            <ArrowLeft className="h-4 w-4" /> {preview ? 'Back to input' : 'Back'}
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Import quote</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Upload an Excel quote onto a job. Parse reads Scope & Pricing totals
            the same way quote-builder does, without AI.
          </p>
        </div>
        {preview && (
          <button
            onClick={() => commitMutation.mutate()}
            disabled={commitMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-60"
          >
            {commitMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save as quote
          </button>
        )}
      </div>

      {!preview ? (
        <ImportInputPanel
          mode={mode} setMode={setMode} text={text} setText={setText}
          file={file} setFile={setFile}
          onParse={() => parseMutation.mutate()} parsing={parseMutation.isPending}
          parseError={parseError}
          jobLabel={jobLabel}
          jobId={jobId}
          onCreateJob={() => { window.location.assign(`${JOBS_HUB}/jobs`) }}
          onImportExisting={(ids) => {
            if (!ids.length) {
              setParseError('Could not copy those quotes.')
              return
            }
            navigate(`/builder/${ids[0]}`)
          }}
        />
      ) : (
        <ImportReviewTable preview={preview} onChange={setPreview} />
      )}
    </div>
  )
}
